<template>
  <span v-if="settings.appBar === 'classic'">
    <NavMenu
        :logo-url="iconPath"
        :menu="menu"
        :emitter="emitter"
    />
    <NavToolbar
        :emitter="emitter"
        :user="user"
        :is-admin="user.hasRole('ROLE_ADMIN')"
        :format-date="formatDate"
        :log-out="handleLogout"
    />
  </span>
    <span v-else>
    <NavBar
        class="navbar"
        :logo-url="logoPath"
        :on-logo-click="handleLogoClick"
        :menu-name="userFullName"
        :hide-menu="!showNavBar"
    >
      <template
        v-if="showNavBar"
        v-slot:actions
      >
        <v-btn
            v-if="isOrg"
            class="action bg-primary"
            elevation="0"
            @click="createProcedure()"
        >{{ $t('Создать процедуру') }}</v-btn>

        <CustomRouterLink
          v-for="item in menu"
          class="actionLink intec-blue--text"
          :class="{ currentAction: isCurrentPage(item.path) }"
          :target="item?.targetBlank === true ? '_blank' : ''"
          :item="item"
        >
          {{ item.name }}
          <children-menu-item :routes="item.children"/>
        </CustomRouterLink>

        <component
          v-if="currentComponent && !isGuest"
          :is="currentComponent.component"
        />
      </template>
      <template
          v-if="isGuest"
          v-slot:right-menu
      >
        <v-btn
          color="primary"
          class="loginButton"
          :variant="loginButtonVariant"
          @click="redirectToLoginPage()"
        >{{ $t('Войти') }}</v-btn>
      </template>
      <template
        v-if="!isGuest"
        v-slot:menu
      >
        <div class="action">
          <UserInfo
              :first-name="user.opts.first_name"
              :middle-name="user.opts.middle_name"
              :last-name="user.opts.last_name"
              :user-role="user.opts.role.name"
              :organization-name="user.opts.contragent ? user.opts.contragent.shortname : null"
          />
        </div>
        <hr class="actionsDivider">
        <div class="actionLink">
          <v-btn
              style="padding: 0px"
              class="menu-blue--text"
              elevation="0"
              @click="openUserProfile()"
          >
            <img src="/bundles/cetpcore/images/logo_profile.svg" style="height: 22px; float: left; padding-right: 10px">{{ $t('Профиль пользователя') }}</v-btn>
        </div>
        <div class="actionLink"
               v-if="user.opts.contragent"
          >
          <v-btn
              style="padding: 0px"
              class="menu-blue--text"
              elevation="0"
              @click="openContragentProfile()"
          >
            <img src="/bundles/cetpcore/images/logo_profile_contragent.svg" style="height: 22px; float: left; padding-right: 10px">{{ $t('Профиль организации') }}</v-btn>
        </div>
        <hr class="actionsDivider" v-if="!isAdmin">
        <v-btn
          v-if="!isAdmin"
          style="padding: 0px"
          class="menu-blue--text"
          elevation="0"
          @click="openMail()"
        >{{ $t('Уведомления') }}</v-btn>
        <hr class="actionsDivider">
        <div class="action">
          <a class="actionLink" href="/auth/logout">{{ $t('Выйти из личного кабинета') }}</a>
        </div>
      </template>
    </NavBar>
  </span>
</template>

<script>
  import api from '@/api';
  import Config from "@/config";
  import {formatDateFromOptions} from "@/filters";
  import {emitter} from "@/app";
  import NavMenu from "@intec/vue-components/navigation/NavMenu.vue";
  import NavToolbar from "@intec/vue-components/navigation/NavToolbar.vue";
  import NavBar from "@intec/vue-components/navigation/NavBar.vue";
  import UserInfo from "@intec/vue-components/info/UserInfo.vue";
  import ChildrenMenuItem from "@/components/childrenMenuItem.vue";
  import { useMenuStore } from "@/stores/menu";
  import { useAppBarComponentStore } from '@/stores/appBarComponent';
  import CustomRouterLink from '@/components/CustomRouterLink';
  import ProcedureCard from "@/components/ProcedureCard.vue";

  export default {
    name: 'AppBar',
    components: {ProcedureCard, ChildrenMenuItem, CustomRouterLink, UserInfo, NavBar, NavToolbar, NavMenu},
    setup() {
      const { menu } = useMenuStore();
      const { currentComponent } = useAppBarComponentStore();

      return {
        currentComponent,
        menu,
      };
    },
    data() {
      let user = Config.getUser();
      let settings = Config.getSettings();
      let isOrg = user.hasRole('ROLE_CUSTOMER');
      let isAdmin = user.hasRole('ROLE_ADMIN');
      let isGuest = Config.isGuest();

      return {
        formatDate: formatDateFromOptions,
        emitter,
        user,
        isOrg,
        isAdmin,
        settings,
        isGuest,
        logoPath: this.$themeData.logoPath,
        iconPath: this.$themeData.iconPath,
        showNavBar: (this.$themeData.showNavBarItemsForGuest && isGuest) || !isGuest,
        loginButtonVariant: this.$themeData.header?.loginButton?.variant ?? 'elevated',
        logoLink: this.$themeData?.header?.bar?.logoLink,
      };
    },
    created() {
      this.emitter.on('profileUpdate', () => {
        this.user = Config.getUser();
      });
    },
    methods: {
      redirectToLoginPage() {
        const route = this.$router.resolve({
          name: 'login',
        });

        window.open(route.href, '_self');
      },
      handleLogout() {
        window.location.href = '/auth/logout';
      },
      handleLogoClick() {
        if (this.logoLink) {
          window.open(this.logoLink, '_blank');
          return;
        }

        this.$router.push('/procedures');
      },
      async createProcedure() {
        try {
          let resp = await api.post('/procedures/create');
          if (resp.procedure_id) {
            this.$router.push({name: 'procedures.publish', params: {id: resp.procedure_id}});
          }
        } catch (e) {
          this.$alerts.error(e);
        }
      },
      async showLogs() {
        try {
          this.$router.push({name: 'admin.logs'});
        } catch (e) {
          this.$alerts.error(e);
        }
      },
      async openUserProfile() {
        try {
          if (this.user.opts.id) {
            this.$router.push('/profile/user');
          }
        } catch (e) {
          this.$alerts.error(e);
        }
      },
      async openContragentProfile() {
        try {
          this.$router.push('/profile/contragent');
        } catch (e) {
          this.$alerts.error(e);
        }
      },
      async openMail() {
        this.$router.push(`/profile/mails`);
      },
      async loadUser(Id) {
        await Config.reload();
        this.user = Config.getUser();
      }
    },
    computed: {
      userFullName() {
        if (Config.isGuest()) {
          return null;
        }

        return this.user.opts.last_name + ' ' +
          this.user.opts.first_name.slice(0, 1) + '.' +
          (this.user.opts.middle_name != null ? this.user.opts.middle_name.slice(0, 1) + '.' : '');
      },
      isCurrentPage() {
        return (path) => {
          return this.$route.path.includes(path);
        };
      }
    },
    watch: {
      $route(toRoute) {
        this.userId = toRoute.params['userId'];
        this.loadUser(this.userId);
      },
    },
    mounted() {
      this.userId = this.$route.params['userId'];
      this.loadUser(this.userId);
    },
  };
</script>

<style scoped>
  .navbar {
    background-color: rgb(var(--primary-color)) !important;
  }

  .action, .actionLink, .menu-blue--text, .loginButton {
    color: rgb(var(--gold-color)) !important;
    background: rgb(var(--primary-color)) !important;
  }

  :deep(.action) {
    color: rgb(var(--gold-color)) !important;
  }

  .menu-blue--text {
    cursor: pointer;
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0;
  }

  .actionLink {
    text-decoration: none;
  }

    .appbar-admin-btn :deep(.v-btn__overlay) {
      background-color: transparent;
    }

    .appbar-admin-btn :deep(.v-btn__content) {
      color: rgb(var(--intec-primary-color));
    }

    .appbar-admin-btn :deep(v-ripple__container) {
      color: #00FF00;
    }

    .appbar-menu-admin-btn {
      background-color: rgb(var(--white-color));
      width: 210px;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      align-items: start;
      border-radius: 8px;
      box-shadow: 0px 1px 3px 0px #00000026;
    }

    .appbar-menu-admin-btn :deep(.v-btn__content) {
      padding: 10px 12.5px;
      font-size: 16px;
      font-weight: 500;
      color: rgb(var(--intec-primary-color)) !important;
    }

    .currentAction {
      text-decoration-line: underline;
      text-underline-offset: 5px;
    }
</style>
